import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { ArrowRight, Copy, Loader2, ShieldCheck } from "lucide-react";
import { useCallback } from "react";
import { toast } from "sonner";
import { OAuthScopePicker } from "./oauth-scope-picker";

export type OAuthAuthorizationScreenProps = {
  authorizationUrl?: string;
  isPolling: boolean;
  scopesSupported?: string[];
  selectedScopes: string[];
  onScopesChange: (scopes: string[]) => void;
  onAuthorize: (scopes: string[]) => void;
  onCompleteLater: () => void;
};

export const OAuthAuthorizationScreen = ({
  authorizationUrl,
  isPolling,
  scopesSupported,
  selectedScopes,
  onScopesChange,
  onAuthorize,
  onCompleteLater,
}: OAuthAuthorizationScreenProps) => {
  const hasDiscoveredScopes =
    scopesSupported !== undefined && scopesSupported.length > 0;

  const handleAuthorize = useCallback(async () => {
    try {
      await onAuthorize(selectedScopes);
    } catch {
      // Error is handled by the parent hook (toast + state reset)
    }
  }, [onAuthorize, selectedScopes]);

  const handleCopy = useCallback(() => {
    if (authorizationUrl) {
      navigator.clipboard.writeText(authorizationUrl);
      toast.success("Authorization URL copied to clipboard");
    }
  }, [authorizationUrl]);

  return (
    <div className="space-y-8 py-6">
      <div className="space-y-4 text-center">
        <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/10">
          {isPolling ? (
            <Loader2 className="size-8 animate-spin text-brand-text" />
          ) : (
            <ShieldCheck className="size-8 text-brand-text" />
          )}
        </div>
        <div className="space-y-2">
          <h2 className="font-semibold text-2xl">
            {isPolling ? "Waiting for Authorization" : "Authorization Required"}
          </h2>
          <p className="mx-auto max-w-sm text-muted-foreground text-sm leading-relaxed">
            {isPolling
              ? "Please complete the authorization in the opened window. This will update automatically once completed."
              : "This MCP server requires authorization to access your resources securely"}
          </p>
        </div>
      </div>

      {hasDiscoveredScopes && !isPolling && (
        <OAuthScopePicker
          onScopesChange={onScopesChange}
          scopesSupported={scopesSupported}
          selectedScopes={selectedScopes}
        />
      )}

      <div className="space-y-3">
        <Button
          className="h-12 w-full gap-2 font-semibold text-base shadow-sm"
          disabled={
            isPolling || (hasDiscoveredScopes && selectedScopes.length === 0)
          }
          onClick={handleAuthorize}
          size="lg"
        >
          {isPolling ? (
            <>
              <Loader2 className="size-5 animate-spin" />
              Waiting for authorization...
            </>
          ) : (
            <>
              Continue to Authorization
              <ArrowRight className="size-5" />
            </>
          )}
        </Button>
        {!!authorizationUrl && (
          <Button
            className="h-10 w-full gap-2"
            onClick={handleCopy}
            size="default"
            variant="outline"
          >
            <Copy className="size-4" />
            Copy Authorization Link
          </Button>
        )}
      </div>

      <div className="border-t pt-4">
        <Button
          className="w-full text-muted-foreground"
          onClick={onCompleteLater}
          size="sm"
          variant="ghost"
        >
          I'll do this later
        </Button>
      </div>
    </div>
  );
};
